import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TYPE_STYLES = {
  info: { accent: "#2563eb", bg: "#eff6ff" },
  success: { accent: "#059669", bg: "#ecfdf5" },
  warning: { accent: "#d97706", bg: "#fffbeb" },
  error: { accent: "#dc2626", bg: "#fef2f2" },
  system: { accent: "#7c3aed", bg: "#f5f3ff" },
};

function ToastItem({ toast, durationMs, onDismiss, onOpen }) {
  const timeoutRef = useRef(null);
  const startedAtRef = useRef(null);
  const remainingMsRef = useRef(durationMs);
  const [isPaused, setIsPaused] = useState(false);

  const style = TYPE_STYLES[toast.type] || TYPE_STYLES.info;

  const startTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    startedAtRef.current = Date.now();
    timeoutRef.current = setTimeout(() => onDismiss(toast.toastId), remainingMsRef.current);
  };

  const pauseTimer = () => {
    if (!timeoutRef.current || isPaused) return;
    clearTimeout(timeoutRef.current);
    const elapsed = Date.now() - (startedAtRef.current || Date.now());
    remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
    setIsPaused(true);
  };

  const resumeTimer = () => {
    if (!isPaused) return;
    setIsPaused(false);
    startTimer();
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <button
      type="button"
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      onClick={() => {
        onOpen(toast);
        onDismiss(toast.toastId);
      }}
      style={{
        width: "340px",
        border: "none",
        borderLeft: `4px solid ${style.accent}`,
        background: style.bg,
        borderRadius: "10px",
        padding: "12px 14px",
        textAlign: "left",
        boxShadow: "0 12px 24px rgba(15, 23, 42, 0.15)",
        cursor: "pointer",
        animation: "slideInToast 0.25s ease",
      }}
      aria-live="polite"
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ color: "#0f172a", fontSize: 14 }}>{toast.title}</strong>
        <span style={{ color: "#64748b", fontSize: 12, textTransform: "capitalize" }}>{toast.type}</span>
      </div>
      <p
        style={{
          margin: "6px 0 0",
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.4,
          maxHeight: "3.8em",
          overflow: "hidden",
        }}
      >
        {toast.message}
      </p>
    </button>
  );
}

export default function NotificationToast({ toasts, durationMs, onDismiss, onOpen }) {
  const sorted = useMemo(() => [...toasts].slice(-5), [toasts]);

  const toastContent = (
    <>
      <style>
        {`
          @keyframes slideInToast {
            from { transform: translateX(20px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          left: "auto",
          bottom: "auto",
          width: "auto",
          padding: "16px",
          maxHeight: "100vh",
          zIndex: 2147483647,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          pointerEvents: "none",
          alignItems: "flex-end",
          justifyContent: "flex-start",
          overflow: "visible",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            overflowX: "visible",
            alignItems: "flex-end",
          }}
        >
          {sorted.map((toast) => (
            <div key={toast.toastId} style={{ pointerEvents: "auto", flexShrink: 0 }}>
              <ToastItem toast={toast} durationMs={durationMs} onDismiss={onDismiss} onOpen={onOpen} />
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return createPortal(toastContent, document.body);
}

